import React, { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Avatar,
  Box,
  Button,
  CssBaseline,
  Paper,
  Typography,
} from '@mui/material'

import anearApi from '../Api/AnearApi'
import CameraDrawer from '../UI/CameraDrawer'
import TokenStorage from '../Api/TokenStorage'
import UserAuthContainer from '../Containers/UserAuthContainer'
import ROUTES from '../../routesConfig'
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'
import { avatarInitial, normalizeAvatarSrc } from '../Utils/avatarSrc'

const tokenStorage = new TokenStorage()

const fieldSx = {
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 2,
  border: '1px solid rgba(148,163,184,0.35)',
  bgcolor: 'rgba(15,23,42,0.8)',
  color: '#E6E9EF',
  px: 1.5,
  py: 1.1,
  fontSize: '1rem',
}

const fieldLabelSx = {
  color: '#A5B4FC',
  fontWeight: 700,
  fontSize: '0.875rem',
  lineHeight: 1.2,
}

const filledButtonSx = {
  textTransform: 'none',
  fontWeight: 800,
  borderRadius: 999,
  px: 3,
  py: 1.3,
  fontSize: '1.05rem',
}

const GuestExpiredPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const userAuth = UserAuthContainer.useContainer()
  const [moreOpen, setMoreOpen] = useState(false)
  const [keeping, setKeeping] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [photoDataUri, setPhotoDataUri] = useState(null)
  const [keepError, setKeepError] = useState('')
  const [leaveError, setLeaveError] = useState('')
  const [photoFailed, setPhotoFailed] = useState(false)
  const snapshot = tokenStorage.getGuestIdentitySnapshot()
  const meta = location.state?.reestablishMeta
  const name = meta?.name || snapshot?.name || 'Your guest profile'
  const avatarUrl = normalizeAvatarSrc(meta?.['avatar-url'] || snapshot?.avatarUrl)
  const continueTo = location.state?.from || { pathname: ROUTES.HOME }

  const submitKeep = async (event) => {
    event.preventDefault()
    setKeepError('')
    const data = new FormData(event.currentTarget)
    const email = String(data.get('email') || '').trim()
    const password = String(data.get('password') || '')
    const nextName = String(data.get('name') || '').trim()
    const attributes = { email, password }
    if (nextName && nextName !== name) attributes.name = nextName
    const userId = meta?.['user-id'] || snapshot?.id

    try {
      const user = await anearApi.updateAccount(userId, attributes)
      // The account is registered and the new token is stored. Drop the
      // expired-guest gate before any navigation, including /home.
      userAuth.dismissGuestReestablish()
      try {
        await userAuth.setUser(user)
      } catch (_sessionErr) {
        // The registered token is already stored. A later load can finish sign-in.
      }
      if (photoDataUri) {
        try {
          await anearApi.updateAvatarImage(user, photoDataUri)
        } catch (_photoErr) {
          setPhotoFailed(true)
          return
        }
      }
      navigate(continueTo)
    } catch (_err) {
      setKeepError('Could not keep this account. Check the email and password and try again.')
    }
  }

  const leaveTo = (path) => {
    navigate(path, { state: { from: continueTo } })
  }

  const adoptRegisteredSession = async () => {
    userAuth.dismissGuestReestablish()
    try {
      const existing = userAuth.anearUser?.()
      const user = existing || await anearApi.getAccount()
      if (user) await userAuth.setUser(user)
    } catch (_sessionErr) {
      // The registered token is already stored.
    }
    navigate(continueTo)
  }

  const relinquishAndLeave = async (path) => {
    setLeaveError('')
    try {
      await anearApi.relinquishExpiredGuest()
    } catch (_err) {
      // Keep already converted this row. Relinquish cannot abandon it.
      if (tokenStorage.hasRegisteredToken()) {
        await adoptRegisteredSession()
        return
      }
      // A previous leave already dropped the bearer, then the gate bounced
      // back here. There is nothing left to relinquish.
      if (!tokenStorage.hasAuthMeta()) {
        userAuth.clearUserAndToken()
        leaveTo(path)
        return
      }
      // The expired guest bearer is still held. Stay until relinquish works.
      setLeaveError('Could not switch accounts. Try again in a moment.')
      return
    }
    userAuth.clearUserAndToken()
    leaveTo(path)
  }

  const jumpInAsSomeoneNew = () => relinquishAndLeave(ROUTES.GUEST_SIGNUP)

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <CameraDrawer
        showCamera={showCamera}
        onPhotoComplete={(dataUri) => {
          setPhotoDataUri(dataUri)
          setShowCamera(false)
        }}
      />
      <Paper
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 3,
          px: 3,
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH,
        }}
      >
        <Typography
          component="h1"
          variant="h5"
          sx={{ color: '#E6E9EF', fontWeight: 800, letterSpacing: 0.2, textAlign: 'center', mb: 2 }}
        >
          Welcome back
        </Typography>

        <Avatar
          src={photoDataUri || avatarUrl || undefined}
          sx={{
            width: 112,
            height: 112,
            mb: 1.25,
            bgcolor: 'rgba(124, 58, 237, 0.3)',
            color: '#A78BFA',
            fontSize: '2.15rem',
            fontWeight: 800,
          }}
        >
          {avatarInitial(name)}
        </Avatar>

        <Typography variant="body1" sx={{ color: 'rgba(230,233,239,0.82)', textAlign: 'center', mb: 2 }}>
          {`Your ${name} guest account has expired, but no worries. You can keep that account.`}
        </Typography>

        {keeping ? (
          <Box component="form" onSubmit={submitKeep} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box component="label" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box component="span" sx={fieldLabelSx}>Name</Box>
              <Box
                component="input"
                name="name"
                defaultValue={name}
                autoComplete="nickname"
                sx={fieldSx}
              />
            </Box>
            <Box component="label" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box component="span" sx={fieldLabelSx}>Email Address</Box>
              <Box
                component="input"
                name="email"
                type="email"
                required
                autoComplete="email"
                sx={fieldSx}
              />
            </Box>
            <Box component="label" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box component="span" sx={fieldLabelSx}>Password</Box>
              <Box
                component="input"
                name="password"
                type="password"
                required
                autoComplete="new-password"
                sx={fieldSx}
              />
            </Box>
            <Button type="button" onClick={() => setShowCamera(true)} sx={{ textTransform: 'none', color: '#A5B4FC' }}>
              Replace photo
            </Button>
            {keepError && (
              <Typography variant="body2" sx={{ color: '#FCA5A5', textAlign: 'center' }}>{keepError}</Typography>
            )}
            {photoFailed && (
              <Button type="button" onClick={() => navigate(continueTo)} sx={{ textTransform: 'none', color: '#E6E9EF' }}>
                Continue
              </Button>
            )}
            <Button type="submit" fullWidth variant="contained" sx={{
              ...filledButtonSx,
              background: 'linear-gradient(90deg,#22C55E,#14B8A6)',
              boxShadow: '0 0 32px rgba(34,197,94,0.35)',
            }}
            >
              {`Keep ${name}`}
            </Button>
          </Box>
        ) : (
          <>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setKeeping(true)}
              sx={{
                ...filledButtonSx,
                background: 'linear-gradient(90deg,#22C55E,#14B8A6)',
                boxShadow: '0 0 32px rgba(34,197,94,0.35)',
              }}
            >
              {`Keep ${name}`}
            </Button>

            <Typography variant="body1" sx={{ mt: 2, mb: 1.25, color: 'rgba(230,233,239,0.82)', textAlign: 'center' }}>
              Or jump in again as someone new. No problem.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={jumpInAsSomeoneNew}
              sx={{
                ...filledButtonSx,
                bgcolor: '#1E293B',
                color: '#E6E9EF',
                boxShadow: 'none',
              }}
            >
              Jump in as someone new
            </Button>
          </>
        )}

        {leaveError && (
          <Typography variant="body2" sx={{ mt: 2, color: '#FCA5A5', textAlign: 'center' }}>{leaveError}</Typography>
        )}

        <Button
          size="small"
          onClick={() => setMoreOpen((open) => !open)}
          sx={{ mt: leaveError ? 1 : 2, color: 'rgba(230,233,239,0.72)', textTransform: 'none' }}
        >
          Not why you&apos;re here?
        </Button>

        {moreOpen && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Button size="small" onClick={() => relinquishAndLeave(ROUTES.SIGNUP)} sx={{ textTransform: 'none', color: '#A5B4FC' }}>Signup</Button>
            <Button size="small" onClick={() => relinquishAndLeave(ROUTES.LOGIN)} sx={{ textTransform: 'none', color: '#A5B4FC' }}>Login</Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default GuestExpiredPage
